import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { currentFinancialYear, P, type PtSlab, type StatutoryConfig } from '@hrms/shared';
import { useAuth } from '../../../auth/AuthProvider';
import { api } from '../../../lib/api';
import { useBreakpoint } from '../../../lib/hooks';
import { useMut } from '../../../lib/query';
import { Alert, Button, Card, Columns, EmptyState, ErrorState, IconButton, Loading, PageHeader, Row, Screen, Segmented, Select, Stack, Switch, Tabs, Text, TextField, useTheme } from '../../../ui';
import { AutoGrid, Chip, clone, FySelect, getIn, MONTHS, NumField, setIn, stateName, stateOptions } from '../components/common';

type Tab = 'pf' | 'esi' | 'pt' | 'lwf' | 'tds' | 'gratuity';
type Regime = 'new_regime' | 'old_regime';
type Path = (string | number)[];

export function StatutoryScreen() {
  const { can } = useAuth();
  const { colors } = useTheme();
  const { isPhone } = useBreakpoint();
  const manage = can(P.PAYROLL_MANAGE);
  const [fy, setFy] = useState(currentFinancialYear());
  const q = useQuery({ queryKey: ['payroll', 'statutory', fy], queryFn: () => api.get<StatutoryConfig>('/statutory-config', { fy }) });
  const [c, setC] = useState<StatutoryConfig | null>(null);
  const [tab, setTab] = useState<Tab>('pf');
  const [ptState, setPtState] = useState('');
  const [regime, setRegime] = useState<Regime>('new_regime');
  const [newSection, setNewSection] = useState('');
  useEffect(() => {
    if (q.data) setC(clone(q.data));
  }, [q.data]);
  useEffect(() => {
    if (c && (!ptState || !c.pt[ptState])) setPtState(Object.keys(c.pt).sort()[0] ?? '');
  }, [c, ptState]);
  const save = useMut(() => api.payroll.saveStatutory({ ...c!, financial_year: fy }), { invalidate: [['payroll', 'statutory']], success: 'Statutory settings saved' });

  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data)
    return (
      <Screen>
        <PageHeader title="Statutory settings" />
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      </Screen>
    );
  if (!c) return <Loading />;

  const set = (path: Path, v: unknown) => setC(setIn(c, path, v));
  const num = (path: Path, label: string, o: { hint?: string; allowEmpty?: boolean } = {}) => (
    <NumField label={label} hint={o.hint} disabled={!manage} allowEmpty={o.allowEmpty} value={getIn(c, path) as number | null} onChange={(v) => set(path, v)} />
  );
  const dirty = JSON.stringify(c) !== JSON.stringify(q.data);
  const pt = ptState ? c.pt[ptState] : undefined;
  const slabs = pt?.slabs ?? [];
  const tds = c.tds[regime];
  const removeKey = (group: 'pt' | 'lwf', key: string) => {
    const n = clone(c);
    delete n[group][key];
    setC(n);
  };

  return (
    <Screen testID="statutory">
      <PageHeader
        title="Statutory settings"
        subtitle={`Financial year ${fy}. Rates are percentages (e.g. 12 for 12%).`}
        actions={
          <>
            <View style={{ width: 160 }}>
              <FySelect value={fy} onChange={setFy} includeNext testID="statutory-fy" />
            </View>
            {manage && <Button title="Discard" disabled={!dirty} onPress={() => setC(clone(q.data!))} />}
            {manage && <Button variant="primary" title="Save changes" disabled={!dirty} loading={save.isPending} onPress={() => save.mutate(undefined)} testID="save-statutory" />}
          </>
        }
      />
      {dirty && <Alert tone="warning">You have unsaved changes. Changes apply to payroll runs processed after saving.</Alert>}
      <Tabs<Tab>
        value={tab}
        onChange={setTab}
        tabs={[
          { key: 'pf', label: 'PF' },
          { key: 'esi', label: 'ESI' },
          { key: 'pt', label: 'Professional tax' },
          { key: 'lwf', label: 'LWF' },
          { key: 'tds', label: 'TDS' },
          { key: 'gratuity', label: 'Gratuity' },
        ]}
      />

      {tab === 'pf' && (
        <Card title="Provident fund (EPF & MP Act)" actions={<Switch label="Enabled" value={c.pf.enabled} disabled={!manage} onChange={(v) => set(['pf', 'enabled'], v)} />}>
          <Stack gap={14}>
            <AutoGrid min={200}>
              {num(['pf', 'employee_rate'], 'Employee rate %')}
              {num(['pf', 'employer_rate'], 'Employer rate %', { hint: 'Total employer share incl. EPS' })}
              {num(['pf', 'eps_rate'], 'EPS rate %')}
              {num(['pf', 'wage_ceiling'], 'Wage ceiling ₹')}
              {num(['pf', 'admin_charge_rate'], 'Admin charges %')}
              {num(['pf', 'edli_rate'], 'EDLI %')}
            </AutoGrid>
            <Switch label="Restrict PF wage to ceiling by default" value={c.pf.apply_ceiling} disabled={!manage} onChange={(v) => set(['pf', 'apply_ceiling'], v)} />
          </Stack>
        </Card>
      )}

      {tab === 'esi' && (
        <Card title="Employees' State Insurance" actions={<Switch label="Enabled" value={c.esi.enabled} disabled={!manage} onChange={(v) => set(['esi', 'enabled'], v)} />}>
          <AutoGrid min={200}>
            {num(['esi', 'employee_rate'], 'Employee rate %')}
            {num(['esi', 'employer_rate'], 'Employer rate %')}
            {num(['esi', 'wage_limit'], 'Gross wage limit ₹ / month')}
          </AutoGrid>
        </Card>
      )}

      {tab === 'pt' && (
        <Columns ratio={[1, 3]}>
          <Card title="States">
            <Stack gap={10}>
              <Row gap={6}>
                {Object.keys(c.pt)
                  .sort()
                  .map((s) => (
                    <Chip key={s} label={`${stateName(s)} (${s})`} selected={s === ptState} onPress={() => setPtState(s)} />
                  ))}
              </Row>
              {!Object.keys(c.pt).length && <Text variant="muted">No PT states configured.</Text>}
              {manage && (
                <Select
                  value={null}
                  placeholder="+ Add state…"
                  options={stateOptions(Object.keys(c.pt))}
                  onChange={(v) => {
                    set(['pt', v], { frequency: 'monthly', slabs: [] });
                    setPtState(v);
                  }}
                  testID="pt-add-state"
                />
              )}
            </Stack>
          </Card>
          {pt ? (
            <Card
              title={`${stateName(ptState)} — PT slabs`}
              subtitle="Monthly gross salary bands. Leave max blank for the top band."
              actions={manage ? <Button size="sm" variant="ghost" icon="trash" title="Remove state" onPress={() => removeKey('pt', ptState)} /> : undefined}
            >
              <Stack gap={12} testID="pt-slabs">
                <Row>
                  <Text variant="label">Frequency</Text>
                  <Segmented
                    value={pt.frequency}
                    onChange={(v) => manage && set(['pt', ptState, 'frequency'], v)}
                    options={[
                      { key: 'monthly', label: 'Monthly' },
                      { key: 'half_yearly', label: 'Half-yearly' },
                    ]}
                  />
                </Row>
                {!slabs.length && <Text variant="muted">No slabs — PT is not deducted in this state.</Text>}
                {slabs.map((s, i) => {
                  const upd = (patch: Partial<PtSlab>) => set(['pt', ptState, 'slabs'], slabs.map((x, j) => (j === i ? { ...x, ...patch } : x)));
                  const lbl = i === 0 || isPhone;
                  return (
                    <Row key={i} gap={8} align="flex-end" style={{ paddingBottom: 8, borderBottomWidth: i < slabs.length - 1 ? 1 : 0, borderBottomColor: colors.border }}>
                      <NumField label={lbl ? 'Min ₹' : undefined} accessibilityLabel="Min" disabled={!manage} value={s.min} onChange={(v) => upd({ min: v ?? 0 })} width={110} />
                      <NumField label={lbl ? 'Max ₹' : undefined} accessibilityLabel="Max" placeholder="∞" allowEmpty disabled={!manage} value={s.max} onChange={(v) => upd({ max: v })} width={110} />
                      <NumField label={lbl ? 'PT ₹' : undefined} accessibilityLabel="Amount" disabled={!manage} value={s.amount} onChange={(v) => upd({ amount: v ?? 0 })} width={90} />
                      <NumField label={lbl ? 'February ₹' : undefined} accessibilityLabel="February amount" placeholder="same" allowEmpty disabled={!manage} value={s.february_amount ?? null} onChange={(v) => upd({ february_amount: v })} width={100} />
                      <View style={{ width: 120 }}>
                        <Select<string>
                          label={lbl ? 'Gender' : undefined}
                          value={s.gender ?? 'all'}
                          disabled={!manage}
                          options={[
                            { value: 'all', label: 'All' },
                            { value: 'female', label: 'Female' },
                          ]}
                          onChange={(v) => upd({ gender: v === 'female' ? 'female' : null })}
                        />
                      </View>
                      {manage && <IconButton icon="x" label="Remove slab" onPress={() => set(['pt', ptState, 'slabs'], slabs.filter((_, j) => j !== i))} />}
                    </Row>
                  );
                })}
                {manage && (
                  <Button
                    icon="plus"
                    title="Add slab"
                    onPress={() => set(['pt', ptState, 'slabs'], [...slabs, { min: (slabs[slabs.length - 1]?.max ?? -1) + 1, max: null, amount: 0 }])}
                    testID="pt-add-slab"
                  />
                )}
              </Stack>
            </Card>
          ) : (
            <Card>
              <EmptyState icon="shield" title="Select or add a state" />
            </Card>
          )}
        </Columns>
      )}

      {tab === 'lwf' && (
        <Card
          title="Labour Welfare Fund"
          actions={
            manage ? (
              <View style={{ width: 220 }}>
                <Select value={null} placeholder="+ Add state…" options={stateOptions(Object.keys(c.lwf))} onChange={(v) => set(['lwf', v], { employee: 0, employer: 0, months: [6, 12] })} testID="lwf-add-state" />
              </View>
            ) : undefined
          }
        >
          <Stack gap={12}>
            {Object.keys(c.lwf).length === 0 && <Text variant="muted">No LWF states configured.</Text>}
            {Object.entries(c.lwf)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([st, v]) => (
                <View key={st} style={{ borderWidth: 1, borderColor: colors.border, borderRadius: 10, padding: 12, gap: 10 }}>
                  <Row justify="space-between">
                    <Text weight="semibold">{`${stateName(st)} (${st})`}</Text>
                    {manage && <IconButton icon="trash" label={`Remove ${st}`} onPress={() => removeKey('lwf', st)} />}
                  </Row>
                  <Row gap={10}>
                    <NumField label="Employee ₹" disabled={!manage} value={v.employee} onChange={(n) => set(['lwf', st, 'employee'], n ?? 0)} width={140} />
                    <NumField label="Employer ₹" disabled={!manage} value={v.employer} onChange={(n) => set(['lwf', st, 'employer'], n ?? 0)} width={140} />
                  </Row>
                  <Text variant="small">Deduction months</Text>
                  <Row gap={6}>
                    {MONTHS.map((m, i) => {
                      const on = v.months.includes(i + 1);
                      return (
                        <Chip
                          key={m}
                          label={m}
                          selected={on}
                          disabled={!manage}
                          onPress={() => set(['lwf', st, 'months'], on ? v.months.filter((x) => x !== i + 1) : [...v.months, i + 1].sort((a, b) => a - b))}
                        />
                      );
                    })}
                  </Row>
                </View>
              ))}
          </Stack>
        </Card>
      )}

      {tab === 'tds' && (
        <Stack>
          <Card
            title="Income tax slabs"
            actions={
              <Segmented<Regime>
                value={regime}
                onChange={setRegime}
                options={[
                  { key: 'new_regime', label: 'New regime' },
                  { key: 'old_regime', label: 'Old regime' },
                ]}
              />
            }
          >
            <Stack gap={14} testID="tds-slabs">
              <AutoGrid min={200}>
                {num(['tds', regime, 'standard_deduction'], 'Standard deduction ₹')}
                {num(['tds', regime, 'rebate_limit'], '87A rebate income limit ₹')}
                {num(['tds', regime, 'rebate_max'], '87A max rebate ₹')}
              </AutoGrid>
              {tds.slabs.map((s, i) => (
                <Row key={i} gap={8} align="center">
                  <Text variant="muted" style={{ width: 80 }}>
                    {i === 0 ? 'Up to ₹' : 'Next up to ₹'}
                  </Text>
                  <NumField accessibilityLabel="Up to" placeholder="∞ (top slab)" allowEmpty disabled={!manage} value={s.upto} onChange={(v) => set(['tds', regime, 'slabs', i, 'upto'], v)} width={150} />
                  <NumField accessibilityLabel="Rate" disabled={!manage} value={s.rate} onChange={(v) => set(['tds', regime, 'slabs', i, 'rate'], v ?? 0)} width={80} />
                  <Text variant="muted">%</Text>
                  {manage && <IconButton icon="x" label="Remove slab" onPress={() => set(['tds', regime, 'slabs'], tds.slabs.filter((_, j) => j !== i))} />}
                </Row>
              ))}
              {manage && <Button icon="plus" title="Add slab" onPress={() => set(['tds', regime, 'slabs'], [...tds.slabs, { upto: null, rate: 0 }])} />}
            </Stack>
          </Card>
          <Columns ratio={[1, 1]}>
            <Card title="Cess">{num(['tds', 'cess_rate'], 'Health & education cess %')}</Card>
            <Card title="Section limits (old regime)">
              <Stack gap={8}>
                {Object.entries(c.tds.section_limits).map(([k, v]) => (
                  <Row key={k} gap={8} wrap={false}>
                    <Text weight="semibold" style={{ width: 90 }}>
                      {k}
                    </Text>
                    <NumField accessibilityLabel={`${k} limit`} disabled={!manage} value={v} onChange={(n) => set(['tds', 'section_limits', k], n ?? 0)} containerStyle={{ flex: 1 }} />
                    {manage && (
                      <IconButton
                        icon="x"
                        label={`Remove ${k}`}
                        onPress={() => {
                          const n = clone(c);
                          delete n.tds.section_limits[k];
                          setC(n);
                        }}
                      />
                    )}
                  </Row>
                ))}
                {manage && (
                  <Row gap={8} wrap={false}>
                    <TextField placeholder="Section e.g. 80TTA" value={newSection} autoCapitalize="characters" onChangeText={(v) => setNewSection(v.toUpperCase())} containerStyle={{ flex: 1 }} accessibilityLabel="New section" />
                    <Button
                      icon="plus"
                      title="Add"
                      disabled={!newSection || newSection in c.tds.section_limits}
                      onPress={() => {
                        set(['tds', 'section_limits', newSection], 0);
                        setNewSection('');
                      }}
                    />
                  </Row>
                )}
              </Stack>
            </Card>
          </Columns>
        </Stack>
      )}

      {tab === 'gratuity' && (
        <Card title="Gratuity">
          <AutoGrid min={220}>
            {num(['gratuity', 'provision_rate'], 'Monthly provision % of basic', { hint: 'Typically 4.81%' })}
            {num(['gratuity', 'max_amount'], 'Max gratuity ₹')}
          </AutoGrid>
        </Card>
      )}
    </Screen>
  );
}
